/**
 * Widget Selector Component
 *
 * Side panel for selecting and adding widgets to the dashboard.
 * Organized by category with search functionality.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useState, useMemo } from 'react';
import {
  X,
  Search,
  Plus,
  ClipboardList,
  PackageSearch,
  Truck,
  Warehouse,
  Send,
  TrendingUp,
  Activity,
  Users,
  Settings,
} from 'lucide-react';
import { useDashboard } from '../DashboardContext';
import { WIDGET_REGISTRY, getWidgetsByCategories } from '../widgetRegistry';
import { WidgetCategory, CATEGORY_INFO, WidgetDefinition } from '../types';

interface WidgetSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

// Map categories to icons
const CATEGORY_ICONS: Record<WidgetCategory, React.ReactNode> = {
  orders: <ClipboardList className="w-4 h-4" />,
  picking: <PackageSearch className="w-4 h-4" />,
  receiving: <Truck className="w-4 h-4" />,
  inventory: <Warehouse className="w-4 h-4" />,
  shipping: <Send className="w-4 h-4" />,
  kpi: <TrendingUp className="w-4 h-4" />,
  activity: <Activity className="w-4 h-4" />,
  operators: <Users className="w-4 h-4" />,
  system: <Settings className="w-4 h-4" />,
};

const WidgetSelector: React.FC<WidgetSelectorProps> = ({ isOpen, onClose }) => {
  const { addWidget, state } = useDashboard();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<WidgetCategory>>(
    new Set<WidgetCategory>(['orders', 'picking', 'kpi'])
  );

  // Get widgets organized by category
  const widgetsByCategory = useMemo(() => getWidgetsByCategories(), []);

  // Filter widgets by search query
  const filteredWidgets = useMemo(() => {
    if (!searchQuery.trim()) return widgetsByCategory;

    const query = searchQuery.toLowerCase();
    const filtered = new Map<WidgetCategory, WidgetDefinition[]>();

    widgetsByCategory.forEach((widgets, category) => {
      const matching = widgets.filter(
        (w) =>
          w.name.toLowerCase().includes(query) ||
          w.description.toLowerCase().includes(query)
      );
      if (matching.length > 0) {
        filtered.set(category, matching);
      }
    });

    return filtered;
  }, [widgetsByCategory, searchQuery]);

  // Check if widget is already in dashboard
  const isWidgetAdded = (widgetId: string) => {
    return state.currentLayout.widgets.some((w) => w.widgetId === widgetId);
  };

  // Toggle category expansion
  const toggleCategory = (category: WidgetCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Handle adding widget
  const handleAddWidget = (widgetId: string) => {
    addWidget(widgetId);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-gray-900 border-l border-gray-700 z-50 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Legg til widget</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Søk etter widgets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Widget list */}
        <div className="flex-1 overflow-y-auto p-4">
          {Array.from(filteredWidgets.entries()).map(([category, widgets]) => {
            const categoryInfo = CATEGORY_INFO[category];
            const isExpanded = expandedCategories.has(category);

            return (
              <div key={category} className="mb-4">
                {/* Category header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <span className="text-gray-400">{CATEGORY_ICONS[category]}</span>
                  <span className="text-sm font-medium text-white flex-1 text-left">
                    {categoryInfo.label}
                  </span>
                  <span className="text-xs text-gray-500">{widgets.length}</span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Widget items */}
                {isExpanded && (
                  <div className="mt-2 space-y-2 ml-2">
                    {widgets.map((widget) => {
                      const isAdded = isWidgetAdded(widget.id);

                      return (
                        <div
                          key={widget.id}
                          className={`
                            p-3 rounded-lg border transition-all
                            ${isAdded
                              ? 'bg-gray-800/50 border-gray-700 opacity-60'
                              : 'bg-gray-800 border-gray-700 hover:border-blue-500/50 hover:bg-gray-750'
                            }
                          `}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-white truncate">
                                {widget.name}
                              </h3>
                              <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                                {widget.description}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-700/50 rounded">
                                  {widget.minWidth}x{widget.minHeight}
                                </span>
                                {widget.defaultRefreshInterval > 0 && (
                                  <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-700/50 rounded">
                                    {widget.defaultRefreshInterval}s
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddWidget(widget.id)}
                              disabled={isAdded}
                              className={`
                                flex-shrink-0 p-2 rounded-lg transition-colors
                                ${isAdded
                                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 hover:bg-blue-500 text-white'
                                }
                              `}
                              title={isAdded ? 'Allerede lagt til' : 'Legg til widget'}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {filteredWidgets.size === 0 && (
            <div className="text-center text-gray-500 py-8">
              <p>Ingen widgets funnet</p>
              <p className="text-sm mt-1">Prøv et annet søkeord</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-800/50">
          <p className="text-xs text-gray-500 text-center">
            {WIDGET_REGISTRY.length} widgets tilgjengelig
          </p>
        </div>
      </div>
    </>
  );
};

export default WidgetSelector;
